import React, { useEffect, useState } from 'react';
import { Typography } from '@material-ui/core';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useDispatch, useSelector } from 'react-redux';

import {
  doLoginApi,
  doVerifyIdpCodeApi,
  getErrorResponse,
  isSandbox,
  isUserLoggedIn,
  resetAuthError,
  resetAuthInformation,
} from '../../store/features/auth';

import LoginForm from './login-form';

import useStyle from './style';

import { getRedirectToSandbox, showSnackbar } from 'helpers/common.helper';
import { useEventContext } from 'providers/events-provider';

import useQuerySearch from 'hooks/use-query-search';

import SignInBg from 'assets/images/fis-SignInBg.svg';
import LayoutImage from 'pages/layout-image/layout-image.page';

const Login: React.FC = () => {
  const classes = useStyle();
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const query = useQuerySearch();
  const dispatch = useDispatch();

  const { setInitialized } = useEventContext();
  const [isCodeVerifying, setIsCodeVerifying] = useState(false);
  const error = useSelector(getErrorResponse);
  const isLoggedIn = useSelector(isUserLoggedIn);
  const sandbox = useSelector(isSandbox);

  useEffect(() => {
    setInitialized(false);
    dispatch(resetAuthInformation());

    // Check for code parameter for IDP verification
    const code = query.get('code');
    const firmName = query.get('firmName');
    if (code) {
      // Get stored email from sessionStorage
      const storedEmail = sessionStorage.getItem('auth-email');
      const storedSandbox = sessionStorage.getItem('auth-sandbox') === 'true';
      verifyIdpCode(code, storedEmail, storedSandbox, firmName);
      // Clear stored values
      sessionStorage.removeItem('auth-email');
      sessionStorage.removeItem('auth-sandbox');
      return;
    }

    // eslint-disable-next-line
  }, []);

  const verifyIdpCode = (
    code: string,
    storedEmail: string,
    storedSandbox: boolean,
    firmName: string,
  ): void => {
    setIsCodeVerifying(true);

    // Dispatch the verification action
    dispatch(
      doVerifyIdpCodeApi({
        code,
        email: storedEmail,
        sandbox: storedSandbox,
        firmName,
      }),
    );

    // Clean up URL
    history.replace(query.get('redirect') ? `/login?redirect=${query.get('redirect')}` : '/login');
  };

  useEffect(() => {
    if (error) {
      dispatch(resetAuthError());
      if (error?.statusCode === 401 && error?.message?.includes('Verify email')) {
        history.replace('/verification');
      } else if (error?.statusCode === 404) {
        renderError(t('DASHBOARD_LOGIN_FAILED'));
        setIsCodeVerifying(false);
      } else if (error?.statusCode === 401 && error?.message?.includes('User is disabled')) {
        renderError(t('CONTACT_ADMIN_TO_REACTIVATE_ACCOUNT'));
        setIsCodeVerifying(false);
      } else {
        const errorExists = error?.message?.length > 0 && i18n.exists(error.message[0]);

        renderError(errorExists ? t(error.message[0]) : t('IDP_VERIFICATION_FAILED'));
        setIsCodeVerifying(false);
      }
    }
    // eslint-disable-next-line
  }, [error]);

  useEffect(() => {
    if (isLoggedIn) {
      if (!sandbox && getRedirectToSandbox()) {
        // if after logging in, we are not in sandbox,
        // it means that the role is not supposed to enter sandbox mode
        history.push('/');
        showSnackbar({
          message: t('DASHBOARD_ENUM_ERROR_MESSAGE_RESTRICTED_SANDBOX'),
          type: 'error',
        });
      } else {
        history.push(query.get('redirect') || sessionStorage.getItem('auth-redirect') || '/');
      }
      sessionStorage.removeItem('auth-redirect');
    }
    // eslint-disable-next-line
  }, [isLoggedIn]);

  const renderError = (msg = t('DASHBOARD_LOGIN_FAILED')): void => {
    showSnackbar({
      message: msg,
      type: 'warning',
    });
  };

  const login = async (values): Promise<void> => {
    const redirectUrl = query.get('redirect');
    if (redirectUrl) {
      sessionStorage.setItem('auth-redirect', redirectUrl);
    }
    dispatch(doLoginApi(values));
  };

  return (
    <LayoutImage image={SignInBg}>
      <div>
        <Typography className={classes.header}>{t('DASHBOARD_SIGNIN_HEADER')}</Typography>
        <Typography className={classes.description}>{t('DASHBOARD_SIGNIN_SUBHEADER')}</Typography>
        <LoginForm login={login} isCodeVerifying={isCodeVerifying} />

        <Typography>
          {t('DASHBOARD_SIGNIN_CTA_SIGNUP')}{' '}
          <Link to="/signup" className={classes.signUpLabel}>
            {t('DASHBOARD_SIGNIN_CTA_SIGNUP_LINK_LABEL')}
          </Link>
        </Typography>
      </div>
    </LayoutImage>
  );
};

export default Login;
